import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { updateMyProfile } from '../api/userProfileApiService';
import { fetchProfile, type UserProfile } from '../api/viewProfileApiService';
import { getPresignedUrlsBatch } from '@/epics/technicianManagement/api/presignedUrlBatchApiService';
import { uploadFileToS3 } from '@/epics/technicianManagement/api/uploadFileToS3';
import type {
  MediaResourcePayload,
  UpdateProfilePayload,
} from '../types/userProfileTypes';

// ─── Form State ──────────────────────────────────────────────────────────────

export interface EditProfileFormData {
  name: string;
  email: string;
  countryCode: string;
  phoneNumber: string;
  addressLine1: string;
  addressLine2: string;
  city: string;
  state: string;
  zipCode: string;
  country: string;
  role: string;
  // Client-specific
  nip: string;
  preferredCurrency: string;
  subscriptionPlan: string;
  paymentStatus: string;
  expiryDate: string;
}

export interface EditProfileErrors {
  name?: string;
  phoneNumber?: string;
  addressLine1?: string;
  city?: string;
  state?: string;
  nip?: string;
  signature?: string;
  contractDocs?: string;
}

// ─── Validation ───────────────────────────────────────────────────────────────

const NIP_REGEX = /^\d{3}-\d{3}-\d{2}-\d{2}$/; // XXX-XXX-XX-XX

const validateForm = (
  formData: EditProfileFormData,
  role: string,
  _profileFile: File | null,
  _existingProfileUrl: string | null,
  signatureFile: File | null,
  existingSignatureUrl: string | null,
  contractFiles: File[],
  existingContractDocs: { filename: string; path: string }[]
): EditProfileErrors => {
  const errors: EditProfileErrors = {};

  if (!formData.name.trim()) {
    errors.name = 'Imię i nazwisko jest wymagane.';
  } else if (formData.name.trim().length > 200) {
    errors.name = 'Imię i nazwisko nie może przekraczać 200 znaków.';
  }

  if (!formData.phoneNumber.trim()) {
    errors.phoneNumber = 'Numer telefonu jest wymagany.';
  } else if (formData.phoneNumber.replace(/\D/g, '').length < 9) {
    errors.phoneNumber = 'Numer telefonu musi zawierać co najmniej 9 cyfr';
  }

  if (!formData.addressLine1.trim()) {
    errors.addressLine1 = 'Adres (linia 1) jest wymagany.';
  }

  if (!formData.city.trim()) {
    errors.city = 'Miejscowość jest wymagana.';
  }

  if (!formData.state.trim()) {
    errors.state = 'Województwo / Region jest wymagane.';
  }

  if (role === 'CLIENT') {
    if (!formData.nip.trim()) {
      errors.nip = 'To pole jest wymagane';
    } else if (!NIP_REGEX.test(formData.nip.trim())) {
      errors.nip = 'Nieprawidłowy NIP';
    }
    const hasContractDocs =
      contractFiles.length > 0 || existingContractDocs.length > 0;
    if (!hasContractDocs) {
      errors.contractDocs = 'To pole jest wymagane';
    }
  }

  if (role === 'TECHNICIAN') {
    const hasSignature =
      signatureFile !== null || existingSignatureUrl !== null;
    if (!hasSignature) {
      errors.signature = 'Podpis jest wymagany.';
    }
  }

  return errors;
};

// ─── Hook ─────────────────────────────────────────────────────────────────────

const FIRST_LOGIN_FLOW_KEY = 'profile-first-login-flow';

export const useEditProfile = () => {
  const navigate = useNavigate();
  const fromFirstLoginRef = useRef(
    sessionStorage.getItem(FIRST_LOGIN_FLOW_KEY) === 'true'
  );

  // Data state
  const [originalUser, setOriginalUser] = useState<UserProfile | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Form state
  const [formData, setFormData] = useState<EditProfileFormData>({
    name: '',
    email: '',
    countryCode: '+48',
    phoneNumber: '',
    addressLine1: '',
    addressLine2: '',
    city: '',
    state: '',
    zipCode: '',
    country: '',
    role: '',
    nip: '',
    preferredCurrency: '',
    subscriptionPlan: '',
    paymentStatus: '',
    expiryDate: '',
  });
  const [errors, setErrors] = useState<EditProfileErrors>({});

  // File state — profile picture
  const [profileFile, setProfileFile] = useState<File | null>(null);
  const [existingProfileUrl, setExistingProfileUrl] = useState<string | null>(
    null
  );
  const [_existingProfilePath, setExistingProfilePath] = useState<
    string | null
  >(null);
  const [profileDeleted, setProfileDeleted] = useState(false);

  // File state — signature (Technician)
  const [signatureFile, setSignatureFile] = useState<File | null>(null);
  const [existingSignatureUrl, setExistingSignatureUrl] = useState<
    string | null
  >(null);
  const [existingSignatureMeta, setExistingSignatureMeta] = useState<{
    path: string;
    resourceId: string;
    filename: string;
    fileType: string;
    fileSize: number;
  } | null>(null);
  const [signatureDeleted, setSignatureDeleted] = useState(false);
  const [signatureUploadProgress, setSignatureUploadProgress] = useState<
    Record<string, number>
  >({});

  // File state — contract docs (Client)
  const [contractFiles, setContractFiles] = useState<File[]>([]);
  const [existingContractDocs, setExistingContractDocs] = useState<
    {
      filename: string;
      path: string;
      fileSize: number;
      resourceId: string;
      fileType: string;
    }[]
  >([]);
  const [deletedContractPaths, setDeletedContractPaths] = useState<string[]>(
    []
  );

  // Snackbar
  const [isSnackbarOpen, setIsSnackbarOpen] = useState(false);
  const [snackbarError, setSnackbarError] = useState('');

  // Success snackbar
  const [showSuccessSnackbar, setShowSuccessSnackbar] = useState(false);

  // Scroll-to-error
  const submitRequestedRef = useRef(false);

  // Cancel modal
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);

  // Snapshot of the form right after the profile is loaded — used to detect
  // "no edits" on cancel so we can skip the confirmation modal.
  const originalFormDataRef = useRef<EditProfileFormData | null>(null);

  // ─── Load Profile ───────────────────────────────────────────────────────────

  const populateFromUser = useCallback((user: UserProfile) => {
    setOriginalUser(user);

    // Backend returns countryCode as ISO code ("PL") for some roles but as
    // dial code ("+48") for others.  The PATCH endpoint always expects the
    // dial-code format, so normalise here.
    const dialCode =
      user.countryCode && /^\+\d/.test(user.countryCode)
        ? user.countryCode
        : '+48';

    const rawPhone =
      user.phoneNumber && user.phoneNumber.startsWith(dialCode)
        ? user.phoneNumber.slice(dialCode.length).trim()
        : (user.phoneNumber ?? '');

    const populated: EditProfileFormData = {
      name: user.name ?? '',
      email: user.email ?? '',
      countryCode: dialCode,
      phoneNumber: rawPhone,
      addressLine1: user.address?.addressLine1 ?? '',
      addressLine2: user.address?.addressLine2 ?? '',
      city: user.address?.city ?? '',
      state: user.address?.state ?? '',
      zipCode: user.address?.zipCode ?? '',
      country: user.address?.country ?? '',
      role: user.role ?? '',
      nip: user.nip ?? '',
      preferredCurrency: user.preferredCurrency ?? '',
      subscriptionPlan: user.subscriptionDetails?.planName ?? '',
      paymentStatus: user.subscriptionDetails?.paymentStatus ?? '',
      expiryDate: user.subscriptionDetails?.expiryDate ?? '',
    };
    setFormData(populated);
    originalFormDataRef.current = populated;

    if (user.profilePicture?.isUploaded && user.profilePicture.url) {
      setExistingProfileUrl(user.profilePicture.url);
      setExistingProfilePath(null);
    }
    if (user.signature?.isUploaded && user.signature.url) {
      setExistingSignatureUrl(user.signature.url);
      setExistingSignatureMeta({
        path: '',
        resourceId: '',
        filename: user.signature.filename ?? '',
        fileType: user.signature.fileType ?? '',
        fileSize: user.signature.fileSize ?? 0,
      });
    }
    if (user.contractDetails?.docs?.length) {
      setExistingContractDocs(
        user.contractDetails.docs
          .filter((d) => d.isUploaded)
          .map((d) => {
            let path = d.path ?? '';
            if (!path && d.url) {
              try {
                path = new URL(d.url).pathname.slice(1);
              } catch {
                path = '';
              }
            }
            let resourceId = d.resourceId ?? '';
            if (!resourceId && path) {
              const match = path.match(
                /\/([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})\//i
              );
              resourceId = match?.[1] ?? '';
            }
            return {
              filename: d.filename,
              path,
              fileSize: d.fileSize ?? 0,
              resourceId,
              fileType: d.fileType ?? '',
            };
          })
      );
    }
  }, []);

  const loadProfile = useCallback(async () => {
    setIsLoading(true);
    try {
      const result = await fetchProfile();
      if (result.success && result.data) {
        populateFromUser(result.data);
      } else {
        setSnackbarError(
          result.error ?? 'Nie udało się załadować profilu. Spróbuj ponownie.'
        );
        setIsSnackbarOpen(true);
      }
    } catch {
      setSnackbarError('Wystąpił nieoczekiwany błąd. Spróbuj ponownie.');
      setIsSnackbarOpen(true);
    } finally {
      setIsLoading(false);
    }
  }, [populateFromUser]);

  useEffect(() => {
    void loadProfile();
  }, [loadProfile]);

  // ─── Field Handlers ─────────────────────────────────────────────────────────

  const handleFieldChange = useCallback(
    (field: keyof EditProfileFormData) =>
      (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const value =
          field === 'phoneNumber' ? e.target.value.slice(0, 9) : e.target.value;
        setFormData((prev) => ({ ...prev, [field]: value }));
        if (errors[field as keyof EditProfileErrors]) {
          setErrors((prev) => ({ ...prev, [field]: undefined }));
        }
      },
    [errors]
  );

  const handleNipChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const digits = e.target.value.replace(/\D/g, '').slice(0, 10);
      let formatted = digits;
      if (digits.length > 8) {
        formatted = `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6, 8)}-${digits.slice(8)}`;
      } else if (digits.length > 6) {
        formatted = `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
      } else if (digits.length > 3) {
        formatted = `${digits.slice(0, 3)}-${digits.slice(3)}`;
      }
      setFormData((prev) => ({ ...prev, nip: formatted }));
      if (errors.nip) {
        setErrors((prev) => {
          const { nip: _n, ...rest } = prev;
          return rest;
        });
      }
    },
    [errors.nip]
  );

  // ─── Profile Picture Handlers ────────────────────────────────────────────────

  const handleProfileFileChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0] ?? null;
      if (file) {
        setProfileFile(file);
        setProfileDeleted(false);
      }
    },
    []
  );

  const handleProfileFileDelete = useCallback(() => {
    setProfileFile(null);
    setExistingProfileUrl(null);
    setExistingProfilePath(null);
    setProfileDeleted(true);
  }, []);

  const handleProfileFileError = useCallback((message: string) => {
    setSnackbarError(message);
    setIsSnackbarOpen(true);
  }, []);

  // ─── Signature Handlers (Technician) ────────────────────────────────────────

  const handleSignatureFileSelect = useCallback((files: File[]) => {
    if (files.length > 0) {
      const file = files[0] ?? null;
      setSignatureFile(file);
      setSignatureDeleted(false);
      setErrors((prev) => {
        const { signature: _s, ...rest } = prev;
        return rest;
      });

      if (file) {
        // Simulate upload progress animation over ~1s
        setSignatureUploadProgress({ [file.name]: 0 });
        const steps = 10;
        const interval = 100; // ms
        let step = 0;
        const timer = setInterval(() => {
          step += 1;
          setSignatureUploadProgress({
            [file.name]: Math.round((step / steps) * file.size),
          });
          if (step >= steps) {
            clearInterval(timer);
            setSignatureUploadProgress({});
          }
        }, interval);
      }
    }
  }, []);

  const handleSignatureExistingDelete = useCallback(() => {
    setExistingSignatureUrl(null);
    setExistingSignatureMeta(null);
    setSignatureDeleted(true);
  }, []);

  const handleSignatureError = useCallback((message: string) => {
    setSnackbarError(message);
    setIsSnackbarOpen(true);
  }, []);

  // ─── Contract Docs Handlers (Client) ────────────────────────────────────────

  const handleContractFilesSelect = useCallback((files: File[]) => {
    setContractFiles(files);
    setErrors((prev) => {
      const { contractDocs: _c, ...rest } = prev;
      return rest;
    });
  }, []);

  const handleContractExistingDelete = useCallback((path: string) => {
    setExistingContractDocs((prev) => prev.filter((d) => d.path !== path));
    setDeletedContractPaths((prev) => [...prev, path]);
  }, []);

  const handleContractError = useCallback((message: string) => {
    setSnackbarError(message);
    setIsSnackbarOpen(true);
  }, []);

  // ─── Cancel Handlers ─────────────────────────────────────────────────────────

  const hasChanges = useMemo(() => {
    const original = originalFormDataRef.current;
    if (!original) {
      return false;
    }
    const fieldsChanged = (
      Object.keys(original) as Array<keyof EditProfileFormData>
    ).some((key) => formData[key] !== original[key]);
    if (fieldsChanged) {
      return true;
    }
    if (profileFile !== null || profileDeleted) {
      return true;
    }
    if (signatureFile !== null || signatureDeleted) {
      return true;
    }
    if (contractFiles.length > 0 || deletedContractPaths.length > 0) {
      return true;
    }
    return false;
  }, [
    formData,
    profileFile,
    profileDeleted,
    signatureFile,
    signatureDeleted,
    contractFiles,
    deletedContractPaths,
  ]);

  const handleCancel = useCallback(() => {
    if (!hasChanges) {
      navigate('/Profil');
      return;
    }
    setIsCancelModalOpen(true);
  }, [hasChanges, navigate]);

  const handleCloseCancelModal = useCallback(() => {
    setIsCancelModalOpen(false);
  }, []);

  const handleConfirmCancel = useCallback(() => {
    setIsCancelModalOpen(false);
    navigate('/Profil');
  }, [navigate]);

  const handleCloseSnackbar = useCallback(() => {
    setIsSnackbarOpen(false);
  }, []);

  const handleCloseSuccessSnackbar = useCallback(() => {
    setShowSuccessSnackbar(false);
  }, []);

  // ─── Submit ──────────────────────────────────────────────────────────────────

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();

      const role = originalUser?.role ?? '';

      const validationErrors = validateForm(
        formData,
        role,
        profileFile,
        existingProfileUrl,
        signatureFile,
        existingSignatureUrl,
        contractFiles,
        existingContractDocs
      );

      if (Object.keys(validationErrors).length > 0) {
        submitRequestedRef.current = true;
        setErrors(validationErrors);
        return;
      }

      setIsSubmitting(true);

      try {
        const userId = originalUser?.clientId ?? 'me';

        // ── Upload helpers (defined inline to capture current state) ──────
        const doUploadProfilePic =
          async (): Promise<MediaResourcePayload | null> => {
            if (!profileFile) {
              return null;
            }
            const paths = [
              `users/${userId}/profiles/resourceid-placeholder/${profileFile.name}`,
            ];
            const batchResult = await getPresignedUrlsBatch({
              paths,
              generatePath: true,
            });
            const fileEntry = batchResult.files[0];
            if (!fileEntry) {
              return null;
            }
            await uploadFileToS3(fileEntry.url, profileFile);
            return {
              isUploaded: true,
              path: fileEntry.path,
              resourceId: fileEntry.resourceId,
              filename: profileFile.name,
              fileType: profileFile.type,
              fileSize: profileFile.size,
            };
          };

        const doUploadSignature =
          async (): Promise<MediaResourcePayload | null> => {
            if (!signatureFile) {
              return null;
            }
            const paths = [
              `users/${userId}/signatures/resourceid-placeholder/${signatureFile.name}`,
            ];
            const batchResult = await getPresignedUrlsBatch({
              paths,
              generatePath: true,
            });
            const fileEntry = batchResult.files[0];
            if (!fileEntry) {
              return null;
            }
            await uploadFileToS3(fileEntry.url, signatureFile);
            return {
              isUploaded: true,
              path: fileEntry.path,
              resourceId: fileEntry.resourceId,
              filename: signatureFile.name,
              fileType: signatureFile.type,
              fileSize: signatureFile.size,
            };
          };

        const doUploadContractDocs = async (): Promise<
          MediaResourcePayload[]
        > => {
          if (contractFiles.length === 0) {
            return [];
          }
          const paths = contractFiles.map(
            (f) => `users/${userId}/docs/resourceid-placeholder/${f.name}`
          );
          const batchResult = await getPresignedUrlsBatch({
            paths,
            generatePath: true,
          });
          await Promise.all(
            contractFiles.map((file, i) => {
              const entry = batchResult.files[i];
              return entry
                ? uploadFileToS3(entry.url, file)
                : Promise.resolve();
            })
          );
          return contractFiles.map((file, i) => ({
            isUploaded: true,
            path: batchResult.files[i]?.path ?? '',
            resourceId: batchResult.files[i]?.resourceId ?? '',
            filename: file.name,
            fileType: file.type,
            fileSize: file.size,
          }));
        };

        // Upload new files
        const [uploadedProfile, uploadedSignature, uploadedContractDocs] =
          await Promise.all([
            doUploadProfilePic(),
            doUploadSignature(),
            doUploadContractDocs(),
          ]);

        // Build payload
        const payload: UpdateProfilePayload = {
          name: formData.name.trim(),
          countryCode: formData.countryCode.trim(),
          phoneNumber: formData.phoneNumber.trim(),
          addressLine1: formData.addressLine1.trim(),
          ...(formData.addressLine2.trim()
            ? { addressLine2: formData.addressLine2.trim() }
            : {}),
          city: formData.city.trim(),
          state: formData.state.trim(),
          ...(formData.zipCode.trim()
            ? { zipCode: formData.zipCode.trim() }
            : {}),
        };

        // Profile pic
        if (uploadedProfile) {
          payload.profilePicture = uploadedProfile;
        } else if (profileDeleted) {
          payload.profilePicture = null;
        }

        // Signature (Technician) — only set when changed; omit to keep existing
        if (role === 'TECHNICIAN') {
          if (uploadedSignature) {
            payload.signature = uploadedSignature;
          } else if (signatureDeleted) {
            payload.signature = null;
          }
        }

        // Client-specific
        if (role === 'CLIENT') {
          payload.nip = formData.nip.trim();

          // Only send contractDocs when the user actually changed them
          // (uploaded new files or deleted existing ones).  Sending existing
          // docs back with empty resourceId / fileType causes backend
          // validation to fail.
          const docsChanged =
            contractFiles.length > 0 || deletedContractPaths.length > 0;
          if (docsChanged) {
            payload.contractDocs = [
              // Keep existing (non-deleted) docs
              ...existingContractDocs.map((d) => ({
                isUploaded: true,
                path: d.path,
                resourceId: d.resourceId,
                filename: d.filename,
                fileType: d.fileType,
                fileSize: d.fileSize,
              })),
              ...uploadedContractDocs,
            ];
          }
        }

        const result = await updateMyProfile(payload);

        if (result.success) {
          const isFirstLogin = fromFirstLoginRef.current;
          if (isFirstLogin) {
            sessionStorage.removeItem(FIRST_LOGIN_FLOW_KEY);
            fromFirstLoginRef.current = false;
          }
          navigate('/Profil', {
            state: {
              successMessage: isFirstLogin
                ? 'Dane profilu zostały dodane.'
                : 'Dane profilu zostały zaktualizowane.',
            },
          });
        } else {
          setSnackbarError(
            result.error ??
              'Nie udało się zaktualizować profilu. Spróbuj ponownie.'
          );
          setIsSnackbarOpen(true);
        }
      } catch {
        setSnackbarError('Wystąpił nieoczekiwany błąd. Spróbuj ponownie.');
        setIsSnackbarOpen(true);
      } finally {
        setIsSubmitting(false);
      }
    },
    [
      formData,
      originalUser,
      profileFile,
      existingProfileUrl,
      profileDeleted,
      signatureFile,
      existingSignatureUrl,
      signatureDeleted,
      contractFiles,
      existingContractDocs,
      deletedContractPaths,
      navigate,
    ]
  );

  return {
    // Data
    originalUser,
    isLoading,
    isSubmitting,

    // Form
    formData,
    errors,
    handleFieldChange,
    handleNipChange,
    submitRequestedRef,

    // Profile pic
    profileFile,
    existingProfileUrl,
    handleProfileFileChange,
    handleProfileFileDelete,
    handleProfileFileError,

    // Signature
    signatureFile,
    existingSignatureUrl,
    existingSignatureMeta,
    signatureUploadProgress,
    handleSignatureFileSelect,
    handleSignatureExistingDelete,
    handleSignatureError,

    // Contract docs
    contractFiles,
    existingContractDocs,
    handleContractFilesSelect,
    handleContractExistingDelete,
    handleContractError,

    // Submit / Cancel
    handleSubmit,
    handleCancel,
    isCancelModalOpen,
    handleCloseCancelModal,
    handleConfirmCancel,

    // Snackbar
    isSnackbarOpen,
    snackbarError,
    handleCloseSnackbar,
    showSuccessSnackbar,
    handleCloseSuccessSnackbar,
  };
};
